import { handleAutoLoanFetchLimits, submitAutoLoanBasicApplication } from 'app/actions/form/actions';
import { ajaxValidateBorrowerResidence, ajaxValidateCoBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { AutoAcknowledgeGapInsurance } from 'app/components/AutoLoan/AutoAcknowledgeGapInsurance';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';
import { FieldNames } from 'app/models/fields/names';
import { Section } from 'app/models/types';
import {
  coBorrowerHasDifferentAddress,
  hasCoBorrower,
  isCoBorrowerEmploymentOther,
  isCoBorrowerRetired,
  isCoEmployee,
  isCoSelfEmployed,
  isEmployee,
  isEmploymentOther,
  isFromDealerAndUsed,
  isHomeowner,
  isRentFree,
  isRenting,
  isRetired,
  isSelfEmployed,
} from '../fields/conditionals';
import { FormParagraphs } from '../paragraphs';
import { autoLoanBasicQuestionById, autoLoanBasicQuestionsByName } from '../questions/auto-loan-basic';
import {
  AUTO_LOAN_BASIC_APP_BORROWER_INCOME_EMPLOYED,
  AUTO_LOAN_BASIC_APP_BORROWER_INCOME_OTHER,
  AUTO_LOAN_BASIC_APP_BORROWER_INCOME_RETIRED,
  AUTO_LOAN_BASIC_APP_BORROWER_INCOME_SOURCE,
  AUTO_LOAN_BASIC_APP_CHOOSE_VEHICLE,
  AUTO_LOAN_BASIC_APP_CITIZENSHIP,
  AUTO_LOAN_BASIC_APP_COBORROWER_HOUSING_INFORMATION,
  AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_EMPLOYED,
  AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_OTHER,
  AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_RETIRED,
  AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_SOURCE,
  AUTO_LOAN_BASIC_APP_COBORROWER_PERSONAL_INFORMATION,
  AUTO_LOAN_BASIC_APP_CURRENT_HOUSING_SITUATION,
  AUTO_LOAN_BASIC_APP_FINAL,
  AUTO_LOAN_BASIC_APP_HOUSING_FREE_INFORMATION,
  AUTO_LOAN_BASIC_APP_HOUSING_OWNER_INFORMATION,
  AUTO_LOAN_BASIC_APP_HOUSING_RENTER_INFORMATION,
  AUTO_LOAN_BASIC_FINAL_PAGE_QUESTION,
  AUTO_LOAN_BASIC_GET_CONTACT_INFORMATION,
  AUTO_LOAN_BASIC_GET_FICO_AND_ZIP,
  AUTO_LOAN_BASIC_GET_LOAN_INFORMATION,
  AUTO_LOAN_BASIC_GET_PURCHASE_PLAN,
  AUTO_LOAN_BASIC_SALVAGE_TITLE_QUESTION,
} from './constants';

export const getAutoLoanBasicSectionById = (id: number): Section => {
  return AUTO_LOAN_BASIC_SECTIONS.find((section) => section.id === id) as Section;
};

const AUTO_LOAN_BASIC_SECTIONS: Section[] = [
  {
    id              : AUTO_LOAN_BASIC_GET_PURCHASE_PLAN,
    questions       : autoLoanBasicQuestionsByName([FieldNames.autoPurchasePlan]),
    hideBackButton  : true,
    hideProgressBar : true,
    title           : 'How are you planning to purchase the vehicle?',
    paragraph       : FormParagraphs.autoPurchasePlan,
  },
  {
    id              : AUTO_LOAN_BASIC_SALVAGE_TITLE_QUESTION,
    questions       : autoLoanBasicQuestionsByName([FieldNames.isVehicleSalvageTitle]),
    showIf          : [isFromDealerAndUsed],
    hideProgressBar : true,
  },
  {
    id           : AUTO_LOAN_BASIC_GET_FICO_AND_ZIP,
    questions    : autoLoanBasicQuestionsByName([FieldNames.propertyZip]),
    submitAction : handleAutoLoanFetchLimits,
    submitText   : 'Next',
  },
  {
    id       : AUTO_LOAN_BASIC_GET_LOAN_INFORMATION,
    questions: autoLoanBasicQuestionsByName([
      FieldNames.purchasePrice,
      FieldNames.tradeInValue,
      FieldNames.downDollar,
      FieldNames.loanTermDesired,
    ]),
    agreement: <AutoAcknowledgeGapInsurance />,
  },
  {
    id       : AUTO_LOAN_BASIC_GET_CONTACT_INFORMATION,
    title    : 'Enter your personal information',
    questions: autoLoanBasicQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
  },
  {
    id       : AUTO_LOAN_BASIC_APP_CHOOSE_VEHICLE,
    title    : 'Tell us about the vehicle you are interested in purchasing',
    questions: autoLoanBasicQuestionsByName([
      FieldNames.vehicleMileage,
      FieldNames.vehicleYear,
      FieldNames.vehicleMake,
      FieldNames.vehicleModel,
    ]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_COBORROWER_PERSONAL_INFORMATION,
    questions : autoLoanBasicQuestionsByName([
      FieldNames.coBorrowerYN,
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
      FieldNames.coBorrowerEmail,
      FieldNames.coBorrowerDOB,
    ]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_CURRENT_HOUSING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : autoLoanBasicQuestionsByName([FieldNames.livingRentOrOwn]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_HOUSING_OWNER_INFORMATION,
    showIf    : [isHomeowner],
    title     : 'Provide your homeowner information',
    questions : autoLoanBasicQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
      FieldNames.livingYearTaxes,
      FieldNames.livingYearInsure,
      FieldNames.livingYearAssociate,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id         : AUTO_LOAN_BASIC_APP_HOUSING_RENTER_INFORMATION,
    showIf     : [isRenting],
    title      : 'Provide your renter information',
    questions  : autoLoanBasicQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id         : AUTO_LOAN_BASIC_APP_HOUSING_FREE_INFORMATION,
    showIf     : [isRentFree],
    title      : 'Provide your housing information',
    questions  : autoLoanBasicQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id        : AUTO_LOAN_BASIC_APP_COBORROWER_HOUSING_INFORMATION,
    showIf    : [hasCoBorrower],
    title     : 'Co-borrower at same address as borrower?',
    questions : autoLoanBasicQuestionsByName([
      FieldNames.coBorrowerSameAddress,
      FieldNames.coBorrowerLivingPropertyStreet,
      FieldNames.coBorrowerLivingPropertyStreet2,
      FieldNames.coBorrowerLivingPropertyCity,
      FieldNames.coBorrowerLivingPropertyState,
      FieldNames.coBorrowerLivingPropertyZip,
    ]),
    submitAction : (formValues) =>
      coBorrowerHasDifferentAddress(formValues) ? ajaxValidateCoBorrowerResidence(formValues) : ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_BORROWER_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : autoLoanBasicQuestionsByName([FieldNames.employmentType]),
  },
  {
    id         : AUTO_LOAN_BASIC_APP_BORROWER_INCOME_EMPLOYED,
    showIf     : [isEmployee, isSelfEmployed],
    title      : 'Tell us about your income and employment',
    questions  : autoLoanBasicQuestionsByName([
      FieldNames.employCompanyName,
      FieldNames.employTitle,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employPrevName,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_BORROWER_INCOME_RETIRED,
    showIf    : [isRetired],
    questions : autoLoanBasicQuestionsByName([FieldNames.retirementIncome]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_BORROWER_INCOME_OTHER,
    showIf    : [isEmploymentOther],
    questions : autoLoanBasicQuestionsByName([FieldNames.otherIncome]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_SOURCE,
    showIf    : [hasCoBorrower],
    title     : `What is your co-borrower's income source?`,
    questions : autoLoanBasicQuestionsByName([FieldNames.coBorrowerEmploymentType]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_EMPLOYED,
    showIf    : [isCoEmployee, isCoSelfEmployed],
    title     : `Tell us about your co-borrower's income and employment`,
    questions : autoLoanBasicQuestionsByName([
      FieldNames.coBorrowerEmployCompanyName,
      FieldNames.coBorrowerEmployTitle,
      FieldNames.coBorrowerEmployStart,
      FieldNames.coBorrowerEmployBase,
      FieldNames.coBorrowerEmployPrevName,
      FieldNames.coBorrowerEmployPrevStart,
      FieldNames.coBorrowerEmployPrevEnd,
    ]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_RETIRED,
    showIf    : [isCoBorrowerRetired],
    questions : autoLoanBasicQuestionsByName([FieldNames.coBorrowerRetirementIncome]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_COBORROWER_INCOME_OTHER,
    showIf    : [isCoBorrowerEmploymentOther],
    questions : autoLoanBasicQuestionsByName([FieldNames.coBorrowerOtherIncome]),
  },
  {
    id        : AUTO_LOAN_BASIC_APP_CITIZENSHIP,
    title     : 'Borrower questions',
    questions : autoLoanBasicQuestionsByName([
      `${FieldNames.decBorrower}.${FieldNames.hasCitizenship}`,
      `${FieldNames.decCoBorrower}.${FieldNames.hasCitizenship}`,
    ]),
    submitAction: submitAutoLoanBasicApplication,
    submitText: 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id                : AUTO_LOAN_BASIC_APP_FINAL,
    questions         : [autoLoanBasicQuestionById(AUTO_LOAN_BASIC_FINAL_PAGE_QUESTION)],
    hideBackButton    : true,
    hideForwardButton : true,
    hideProgressBar   : true,
  },
];
